import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { ReactNode } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { ACE_STEPS, AceSelectOption } from './ace-trip-form'

type AceSectionProps = {
  title: string
  action?: ReactNode
  children: ReactNode
}

export const AceSection = ({ title, action, children }: AceSectionProps) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      p: 2,
      bgcolor: 'common.white',
    }}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: 2 }}
    >
      <Typography fontWeight={700} color="primary.main" fontSize={16}>
        {title}
      </Typography>
      {action}
    </Stack>
    {children}
  </Box>
)

type AceSelectFieldProps = {
  name: string
  label: string
  options: AceSelectOption[]
  placeholder?: string
}

export const AceSelectField = ({
  name,
  label,
  options,
  placeholder = 'Select',
}: AceSelectFieldProps) => {
  const { control } = useFormContext()

  return (
    <Stack spacing={1}>
      <InputLabel sx={{ color: 'secondary.dark', fontWeight: 300 }}>
        {label}
      </InputLabel>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth size="small" error={!!error}>
            <Select
              {...field}
              displayEmpty
              sx={{ borderRadius: '6px' }}
              MenuProps={{
                sx: { zIndex: (theme) => theme.zIndex.modal + 20 },
              }}
            >
              <MenuItem value="">
                <Typography color="text.secondary">{placeholder}</Typography>
              </MenuItem>
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {error?.message ? (
              <FormHelperText>{error.message}</FormHelperText>
            ) : null}
          </FormControl>
        )}
      />
    </Stack>
  )
}

type AceStepperProps = {
  value: number
  onChange: (index: number) => void
}

export const AceStepper = ({ value, onChange }: AceStepperProps) => (
  <Stack
    direction="row"
    sx={{
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    {ACE_STEPS.map((step, index) => {
      const isActive = value === index
      return (
        <Box
          key={step}
          role="tab"
          aria-selected={isActive}
          onClick={() => onChange(index)}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1,
            py: 1.5,
            cursor: 'pointer',
            borderBottom: '3px solid',
            borderColor: isActive ? 'primary.main' : 'transparent',
            mb: '-1px',
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: isActive ? 'primary.main' : 'grey.400',
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 14,
              flexShrink: 0,
            }}
          >
            {index + 1}
          </Box>
          <Typography
            fontWeight={700}
            color={isActive ? 'primary.main' : 'text.secondary'}
          >
            {step}
          </Typography>
        </Box>
      )
    })}
  </Stack>
)
